import { TranscriptEntry } from './index';

export type { TranscriptEntry };

export interface AICoachingMessage {
	id: string;
	text: string;
	timestamp: Date;
	isStreaming: boolean;
	role: 'user' | 'assistant'; // Distinguish transcript/questions from AI responses
	messageType?: 'transcript' | 'question' | 'system'; // For user messages: transcript sent, direct question, or system notification
}

export interface TysonCoachingContextData {
	phoneCallId?: string;
	companyId: string;
	leadServedId?: string; // Optional for practice calls (tyson-practice mode)
	contactId: string;
	objectivePrompt?: string; // Coaching system prompt from backend
	agentTemplateId?: string; // Optional: specific coaching agent template to use
}

export interface TranscriptBatch {
	entries: TranscriptEntry[];
	timestamp: Date;
}

export interface LiveUtterance {
	sequenceId: string;
	speaker: 'Lead' | 'Sales Rep';
	transcription: string;
	final: boolean;
	timestamp: Date;
	// New fields for partial utterance handling
	stableText?: string;
	speculativeText?: string;
}

/**
 * Partial utterance tracking for real-time transcription.
 * Handles out-of-order events and stability pairs from Twilio.
 */
export interface PartialUtterance {
	sequenceId: number; // Parsed from string for ordering
	speaker: 'Lead' | 'Sales Rep';
	stableText: string; // stability >= 0.5
	speculativeText: string; // stability < 0.5
	final: boolean;
	timestamp: Date;
	highestSeqSeen: number; // For out-of-order rejection
}

export const TYSON_ASSISTANT_NAME = 'SalesLiveCoachingAssistant';

export const TYSON_COACHING_FALLBACK_PROMPT = `You are an expert sales coach providing real-time guidance during a real or practice sales call.

Your role:
- Provide concise, actionable suggestions as the conversation unfolds
- Highlight what the sales rep is doing well
- Suggest specific improvements when the rep misses opportunities
- Be encouraging but direct
- Focus on practical next steps the rep can take RIGHT NOW

Format your coaching as brief, scannable suggestions. Use markdown for emphasis.`;

export const TYSON_END_OF_CALL_SUMMARY_PROMPT = `The call has ended. Based on the entire conversation transcript and our training materials, please provide a comprehensive summary:

## What Went Well
- Highlight 2-3 specific things the SDR did effectively

## Areas for Improvement
- Identify 2-3 specific areas where the SDR could improve
- Reference relevant techniques from our training materials

## Focus for Next Call
- Provide 1-2 actionable items to practice on the next call

Be specific and reference actual moments from the call.`;
